/**
 * useTouchDragDrop - Mobile long-press drag-and-drop for calendar events
 *
 * Intercepts touch gestures on .rbc-event elements inside a container ref.
 * After a 500ms long-press, enters drag mode with a floating clone that
 * follows the finger. On drop, calculates the target time slot and calls
 * the provided onEventDrop callback.
 */

import { useEffect, useRef, useCallback, RefObject } from 'react';

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  [key: string]: any;
}

interface UseTouchDragDropOptions {
  containerRef: RefObject<HTMLDivElement | null>;
  events: CalendarEvent[];
  onEventDrop: (args: { event: CalendarEvent; start: Date; end: Date }) => void;
  enabled: boolean;
  longPressDelay?: number;
  calendarMin?: Date; // the min prop from react-big-calendar (hour the calendar starts)
}

export function useTouchDragDrop({
  containerRef,
  events,
  onEventDrop,
  enabled,
  longPressDelay = 500,
  calendarMin,
}: UseTouchDragDropOptions) {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDragging = useRef(false);
  const draggedEvent = useRef<CalendarEvent | null>(null);
  const cloneEl = useRef<HTMLElement | null>(null);
  const startTouch = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const eventDuration = useRef<number>(0);
  const highlightedSlot = useRef<HTMLElement | null>(null);

  // The hour the calendar starts at (default 6 AM)
  const minHour = calendarMin ? calendarMin.getHours() : 6;

  const cleanup = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (cloneEl.current) {
      cloneEl.current.remove();
      cloneEl.current = null;
    }
    if (highlightedSlot.current) {
      highlightedSlot.current.classList.remove('rbc-touch-drop-target');
      highlightedSlot.current = null;
    }
    document.body.classList.remove('rbc-touch-drag-active');
    isDragging.current = false;
    draggedEvent.current = null;
  }, []);

  const findEventFromElement = useCallback(
    (el: HTMLElement): CalendarEvent | null => {
      // Walk up to the .rbc-event element
      const eventEl = el.closest('.rbc-event') as HTMLElement | null;
      if (!eventEl) return null;

      // Try to match by title text content
      const titleText = eventEl.textContent?.trim();
      if (!titleText) return null;

      // Find the event whose title appears in this element's text
      const match = events.find((ev) => titleText.includes(ev.title));
      return match || null;
    },
    [events]
  );

  const getTimeFromPoint = useCallback(
    (x: number, y: number, container: HTMLElement): { date: Date; dayColumn: HTMLElement } | null => {
      // Hide the clone temporarily so elementFromPoint can see through it
      if (cloneEl.current) {
        cloneEl.current.style.display = 'none';
      }
      const elUnder = document.elementFromPoint(x, y) as HTMLElement | null;
      if (cloneEl.current) {
        cloneEl.current.style.display = '';
      }

      if (!elUnder) return null;

      // Find the day column (.rbc-day-slot) this point is inside
      const daySlot = elUnder.closest('.rbc-day-slot') as HTMLElement | null;
      if (!daySlot) return null;

      // Get all time slots in this day column
      const timeSlots = daySlot.querySelectorAll('.rbc-time-slot');
      if (timeSlots.length === 0) return null;

      // Determine date from the day column header
      // react-big-calendar puts the date in the header cells
      const daySlotRect = daySlot.getBoundingClientRect();
      const content = daySlot.closest('.rbc-time-content') as HTMLElement | null;
      if (!content) return null;

      // Find which column index this day slot is
      const allDaySlots = content.querySelectorAll('.rbc-day-slot');
      let columnIndex = -1;
      allDaySlots.forEach((slot, i) => {
        if (slot === daySlot) columnIndex = i;
      });

      // Get the date from the corresponding header cell
      const headerRow = container.querySelector('.rbc-time-header .rbc-row');
      if (!headerRow) return null;

      // Header cells: first child might be gutter, then day headers
      const headerCells = headerRow.querySelectorAll('.rbc-header');
      const headerCell = headerCells[columnIndex] as HTMLElement | null;

      let targetDate: Date;
      if (headerCell) {
        // Try to get date from the header's anchor or button
        const dateLink = headerCell.querySelector('a, button, span');
        const headerText = dateLink?.textContent || headerCell.textContent || '';

        // react-big-calendar header format varies; try to parse
        // In week view, headers show day abbreviation + date number
        // We'll use the calendar's visible date range to compute
        // For now, try matching from the header's date attributes
        const dateAttr = headerCell.getAttribute('data-date');
        if (dateAttr) {
          targetDate = new Date(dateAttr);
        } else {
          // Fallback: use the current date for day view,
          // or compute from column index for week view
          targetDate = new Date();
          // Try to extract date number from header text
          const dateMatch = headerText.match(/(\d+)/);
          if (dateMatch) {
            const dayNum = parseInt(dateMatch[1], 10);
            // Get the first visible date from the first header
            const firstHeader = headerCells[0];
            const firstDateAttr = firstHeader?.getAttribute('data-date');
            if (firstDateAttr) {
              targetDate = new Date(firstDateAttr);
              targetDate.setDate(targetDate.getDate() + columnIndex);
            } else {
              targetDate.setDate(dayNum);
            }
          }
        }
      } else {
        targetDate = new Date();
      }

      // Calculate the time from Y position within the day column
      // The time content area spans from minHour to max hour (typically 6 AM to midnight)
      const totalSlots = timeSlots.length; // total number of 30-min slots rendered
      const firstSlotRect = (timeSlots[0] as HTMLElement).getBoundingClientRect();
      const lastSlotRect = (timeSlots[timeSlots.length - 1] as HTMLElement).getBoundingClientRect();
      const totalHeight = lastSlotRect.bottom - firstSlotRect.top;

      const relativeY = y - firstSlotRect.top;
      const fraction = Math.max(0, Math.min(1, relativeY / totalHeight));

      // Each slot is typically 30 minutes
      // Total hours displayed = totalSlots * 0.5
      const totalHours = totalSlots * 0.5;
      const hoursFromMin = fraction * totalHours;
      const targetHour = minHour + Math.floor(hoursFromMin);
      const targetMinute = Math.round((hoursFromMin % 1) * 60 / 15) * 15; // snap to 15-min increments

      const result = new Date(targetDate);
      result.setHours(targetHour, targetMinute, 0, 0);

      return { date: result, dayColumn: daySlot };
    },
    [minHour]
  );

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;

    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const eventEl = target.closest('.rbc-event') as HTMLElement | null;
      if (!eventEl) return;

      const touch = e.touches[0];
      startTouch.current = { x: touch.clientX, y: touch.clientY };

      const event = findEventFromElement(eventEl);
      if (!event) return;

      // Start long-press timer
      longPressTimer.current = setTimeout(() => {
        // Entered drag mode
        isDragging.current = true;
        draggedEvent.current = event;
        eventDuration.current = event.end.getTime() - event.start.getTime();

        // Haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }

        // Prevent scrolling
        document.body.classList.add('rbc-touch-drag-active');

        // Create floating clone
        const rect = eventEl.getBoundingClientRect();
        const clone = eventEl.cloneNode(true) as HTMLElement;
        clone.classList.add('rbc-event-dragging');
        clone.style.position = 'fixed';
        clone.style.left = `${rect.left}px`;
        clone.style.top = `${rect.top}px`;
        clone.style.width = `${rect.width}px`;
        clone.style.height = `${rect.height}px`;
        clone.style.zIndex = '10000';
        clone.style.pointerEvents = 'none';
        document.body.appendChild(clone);
        cloneEl.current = clone;

        // Dim the original event
        eventEl.style.opacity = '0.3';
      }, longPressDelay);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];

      if (!isDragging.current) {
        // Check if finger moved too much before long press triggered
        const dx = touch.clientX - startTouch.current.x;
        const dy = touch.clientY - startTouch.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > 10) {
          // Cancel long-press — user is scrolling
          if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
          }
        }
        return;
      }

      // In drag mode — prevent scroll
      e.preventDefault();

      // Move the clone
      if (cloneEl.current) {
        const cloneWidth = cloneEl.current.offsetWidth;
        const cloneHeight = cloneEl.current.offsetHeight;
        cloneEl.current.style.left = `${touch.clientX - cloneWidth / 2}px`;
        cloneEl.current.style.top = `${touch.clientY - cloneHeight / 2}px`;
      }

      // Highlight target slot
      if (highlightedSlot.current) {
        highlightedSlot.current.classList.remove('rbc-touch-drop-target');
        highlightedSlot.current = null;
      }

      // Hide clone to see what's underneath
      if (cloneEl.current) cloneEl.current.style.display = 'none';
      const elUnder = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
      if (cloneEl.current) cloneEl.current.style.display = '';

      if (elUnder) {
        const slot = elUnder.closest('.rbc-time-slot') as HTMLElement | null;
        if (slot) {
          slot.classList.add('rbc-touch-drop-target');
          highlightedSlot.current = slot;
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }

      if (!isDragging.current || !draggedEvent.current) {
        cleanup();
        return;
      }

      const touch = e.changedTouches[0];
      const result = getTimeFromPoint(touch.clientX, touch.clientY, container);

      // Restore opacity on original event elements
      const allEvents = container.querySelectorAll('.rbc-event');
      allEvents.forEach((el) => {
        (el as HTMLElement).style.opacity = '';
      });

      if (result) {
        const newStart = result.date;
        const newEnd = new Date(newStart.getTime() + eventDuration.current);

        onEventDrop({
          event: draggedEvent.current,
          start: newStart,
          end: newEnd,
        });
      }

      cleanup();
    };

    const handleTouchCancel = () => {
      // Restore opacity on original event elements
      const allEvents = container.querySelectorAll('.rbc-event');
      allEvents.forEach((el) => {
        (el as HTMLElement).style.opacity = '';
      });
      cleanup();
    };

    // Use passive: false on touchmove so we can preventDefault during drag
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchCancel);
      cleanup();
    };
  }, [enabled, containerRef, events, onEventDrop, longPressDelay, findEventFromElement, getTimeFromPoint, cleanup]);
}
